import type { Fact, StrategyId } from "../model.ts";

export function factExpression(fact: Fact): string {
  return `${fact.left} ${fact.operation === "ADD" ? "+" : "−"} ${fact.right}`;
}

/** Teaching examples follow the chosen strategy, not a different overlapping fact tag. */
export function strategyWorkedExample(strategyId: StrategyId, fact: Fact): readonly string[] {
  const { left, right, answer, operation } = fact;
  const equation = `${factExpression(fact)} = ${answer}.`;
  switch (strategyId) {
    case "ZERO_IDENTITY":
      return [operation === "SUBTRACT" && left === right
        ? "Haal je alles weg, dan blijft nul over."
        : "Nul erbij of nul eraf verandert het andere getal niet.", equation];
    case "COUNT_ON": {
      const start = Math.max(left, right);
      const steps = Math.min(left, right);
      return [`Begin bij ${start}. Tel ${steps} verder: ${Array.from({ length: steps }, (_, i) => start + i + 1).join(", ")}.`, equation];
    }
    case "COUNT_BACK":
      return [`Begin bij ${left}. Tel ${right} terug: ${Array.from({ length: right }, (_, i) => left - i - 1).join(", ")}.`, equation];
    case "DOUBLES":
      return [operation === "ADD"
        ? `Twee groepjes van ${left} zijn samen ${answer}.`
        : `${right} + ${right} = ${left}. Eén groepje eraf: één groepje blijft over.`, equation];
    case "MAKE_10":
      return [operation === "ADD"
        ? `${left} heeft nog ${right} nodig om 10 te worden.`
        : `${right} + ${answer} = 10. Haal ${right} weg; het andere deel blijft over.`, equation];
    case "NEAR_DOUBLE": {
      const base = operation === "ADD" ? Math.min(left, right) : right;
      const delta = operation === "ADD" ? 1 : left - right * 2;
      return [`${base} + ${base} = ${base * 2}.`,
        operation === "ADD" ? "Bij deze som komt daar nog 1 bij."
          : `Het begingetal is ${delta > 0 ? "1 meer" : "1 minder"} dan dat dubbel. Het antwoord is dus ook ${delta > 0 ? "1 meer" : "1 minder"} dan ${base}.`, equation];
    }
    case "COMMUTE":
      return [`${right} + ${left} = ${answer}. Wissel de getallen om; het totaal blijft gelijk.`, equation];
    case "INVERSE_FACT":
      return [operation === "ADD"
        ? `${answer} − ${right} = ${left}. Doe het weggehaalde deel er weer bij.`
        : `${answer} + ${right} = ${left}. Haal het tweede deel weer weg.`, equation];
    case "TEEN_ANALOGY": {
      const teen = operation === "ADD" ? Math.max(left, right) : left;
      const small = operation === "ADD" ? Math.min(left, right) : right;
      const sign = operation === "ADD" ? "+" : "−";
      return [`${teen} is 10 en ${teen - 10}. Laat de 10 staan.`,
        `${teen - 10} ${sign} ${small} = ${answer - 10}.`, `10 + ${answer - 10} = ${answer}.`, equation];
    }
    case "VIA_10_ADD": {
      const toTen = 10 - left;
      const rest = right - toTen;
      return [`${left} heeft ${toTen} nodig om 10 te worden.`,
        `Splits ${right} in ${toTen} en ${rest}.`, `${left} + ${toTen} = 10.`, `10 + ${rest} = ${answer}.`, equation];
    }
    case "BACK_VIA_10": {
      const toTen = left - 10;
      const rest = right - toTen;
      return [`Splits ${right} in ${toTen} en ${rest}.`,
        `${left} − ${toTen} = 10.`, `10 − ${rest} = ${answer}.`, equation];
    }
    default: throw new Error(`Geen lesuitwerking voor strategie ${strategyId}.`);
  }
}

export function strategyGuidance(strategyId: StrategyId, fact: Fact): readonly string[] {
  switch (strategyId) {
    case "COUNT_ON": return [`Begin bij ${Math.max(fact.left, fact.right)} en tel ${Math.min(fact.left, fact.right)} verder.`];
    case "COUNT_BACK": return [`Begin bij ${fact.left} en tel ${fact.right} terug.`];
    case "ZERO_IDENTITY": return ["Gebruik de nulregel: niets erbij, niets eraf of alles weg."];
    case "DOUBLES": return ["Denk aan twee even grote groepjes."];
    case "MAKE_10": return ["Welke twee delen maken samen 10?"];
    case "NEAR_DOUBLE": return ["Gebruik een dubbel en pas het antwoord met één aan."];
    case "COMMUTE": return ["Wissel de getallen om. Het totaal blijft gelijk."];
    case "INVERSE_FACT": return ["Gebruik de plus- en minsom die bij deze drie getallen horen."];
    case "TEEN_ANALOGY": return ["Laat de 10 staan en reken met het stukje erna."];
    case "VIA_10_ADD": return ["Maak eerst 10 en tel daarna de rest erbij."];
    case "BACK_VIA_10": return ["Ga eerst terug naar 10 en haal daarna de rest eraf."];
    default: throw new Error(`Geen lesbegeleiding voor strategie ${strategyId}.`);
  }
}
